import {
    Component,
    ContentChildren,
    EventEmitter,
    forwardRef,
    HostListener,
    Injector,
    Input,
    QueryList,
    TemplateRef,
    Type,
    ViewChild,
} from '@angular/core';

// rx

import { Observable } from 'rxjs/Observable';
import'rxjs/add/operator/map';
import'rxjs/add/operator/filter';
import'rxjs/add/operator/first';
import'rxjs/add/operator/debounceTime';

import { Ng2Dropdown, Ng2MenuItem } from 'ng2-material-dropdown';
import { OptionsProvider } from '../../core/providers/options-provider';
import { TagInputComponent } from '../tag-input/tag-input';
import { TagInputDropdownOptions } from '../../defaults';
import { TagModel } from '../../core/accessor';

const /** @type {?} */ defaults: Type<TagInputDropdownOptions> = forwardRef(() => OptionsProvider.defaults.dropdown);
export class TagInputDropdown {
/**
 * \@name dropdown
 */
public dropdown: Ng2Dropdown;
/**
 * \@name menuTemplate
 * @desc reference to the template if provided by the user
 */
public templates: QueryList<TemplateRef<any>>;
/**
 * \@name offset
 */
public offset: string = new defaults().offset;
/**
 * \@name focusFirstElement
 */
public focusFirstElement = new defaults().focusFirstElement;
/**
 * - show autocomplete dropdown if the value of input is empty
 * \@name showDropdownIfEmpty
 */
public showDropdownIfEmpty = new defaults().showDropdownIfEmpty;
/**
 * \@description observable passed as input which populates the autocomplete items
 * \@name autocompleteObservable
 */
public autocompleteObservable: (text: string) => Observable<any>;
/**
 * - desc minimum text length in order to display the autocomplete dropdown
 * \@name minimumTextLength
 */
public minimumTextLength = new defaults().minimumTextLength;
/**
 * - number of items to display in the autocomplete dropdown
 * \@name limitItemsTo
 */
public limitItemsTo: number = new defaults().limitItemsTo;
/**
 * \@name displayBy
 */
public displayBy = new defaults().displayBy;
/**
 * \@name identifyBy
 */
public identifyBy = new defaults().identifyBy;
/**
 * \@description a function a developer can use to implement custom matching for the autocomplete
 * \@name matchingFn
 */
public matchingFn: (value: string, target: TagModel) => boolean = new defaults().matchingFn;
/**
 * \@name appendToBody
 */
public appendToBody = new defaults().appendToBody;
/**
 * \@name keepOpen
 * \@description option to leave dropdown open when adding a new item
 */
public keepOpen = new defaults().keepOpen;
/**
 * list of items that match the current value of the input (for autocomplete)
 * \@name items
 */
public items: TagModel[] = [];
/**
 * \@name tagInput
 */
public tagInput: TagInputComponent = this.injector.get(TagInputComponent);
/**
 * \@name _autocompleteItems
 */
private _autocompleteItems: TagModel[] = [];
/**
 * \@name autocompleteItems
 * @param {?} items
 * @return {?}
 */
public set autocompleteItems(items: TagModel[]) {
        this._autocompleteItems = items;
    }
/**
 * \@name autocompleteItems
 * @desc array of items that will populate the autocomplete
 * @return {?}
 */
public get autocompleteItems(): TagModel[] {
        const /** @type {?} */ items = this._autocompleteItems;

        if (!items) {
            return [];
        }

        return items.map((item: TagModel) => {
            return typeof item === 'string' ? {
                [this.displayBy]: item,
                [this.identifyBy]: item
            } : item;
        });
    }
/**
 * @param {?} injector
 */
constructor(private readonly injector: Injector) {}
/**
 * \@name ngOnInit
 * @return {?}
 */
public ngOnInit(): void {
        this.onItemClicked().subscribe(this.requestAdding);

        // reset itemsMatching array when the dropdown is hidden
        this.onHide().subscribe(this.resetItems);

        const /** @type {?} */ DEBOUNCE_TIME = 200;
        const /** @type {?} */ KEEP_OPEN = this.keepOpen;

        this.tagInput
            .onTextChange
            .asObservable()
            .debounceTime(DEBOUNCE_TIME)
            .filter((value: string) => {
                if (KEEP_OPEN === false) {
                    return value.length > 0;
                }

                return true;
            })
            .subscribe(this.show);
    }
/**
 * \@name updatePosition
 * @return {?}
 */
public updatePosition(): void {
        const /** @type {?} */ position = this.tagInput.inputForm.getElementPosition();

        this.dropdown.menu.updatePosition(position);
    }
/**
 * \@name isVisible
 * @return {?}
 */
public get isVisible(): boolean {
        return this.dropdown.menu.state.menuState.isVisible;
    }
/**
 * \@name onHide
 * @return {?}
 */
public onHide(): EventEmitter<Ng2Dropdown> {
        return this.dropdown.onHide;
    }
/**
 * \@name onItemClicked
 * @return {?}
 */
public onItemClicked(): EventEmitter<string> {
        return this.dropdown.onItemClicked;
    }
/**
 * \@name selectedItem
 * @return {?}
 */
public get selectedItem(): Ng2MenuItem {
        return this.dropdown.menu.state.dropdownState.selectedItem;
    }
/**
 * \@name state
 * @return {?}
 */
public get state(): any {
        return this.dropdown.menu.state;
    }
/**
 *
 * \@name show
 */
public show = (): void => {
        const value = this.getFormValue();
        const hasMinimumText = value.trim().length >= this.minimumTextLength;
        const position = this.calculatePosition();
        const items = this.getMatchingItems(value);
        const hasItems = items.length > 0;
        const isHidden = this.isVisible === false;
        const showDropdownIfEmpty = this.showDropdownIfEmpty && hasItems && !value;
        const assertions = [];

        const shouldShow = isHidden && ((hasItems && hasMinimumText) || showDropdownIfEmpty);
        const shouldHide = this.isVisible && !hasItems;

        if (this.autocompleteObservable && hasMinimumText) {
            return this.getItemsFromObservable(value);
        }

        if (!this.showDropdownIfEmpty && !value) {
            return this.dropdown.hide();
        }

        this.setItems(items);

        if (shouldShow) {
            this.dropdown.show(position);
        } else if (shouldHide) {
            this.hide();
        }
    }
/**
 * \@name hide
 * @return {?}
 */
public hide(): void {
        this.resetItems();
        this.dropdown.hide();
    }
/**
 * \@name scrollListener
 * @return {?}
 */
public scrollListener(): void {
        if (!this.isVisible) {
            return;
        }

        this.updatePosition();
    }
/**
 * \@name onWindowBlur
 * @return {?}
 */
public onWindowBlur(): void {
        this.dropdown.hide();
    }
/**
 * \@name getFormValue
 * @return {?}
 */
private getFormValue(): string {
        return this.tagInput.formValue.trim();
    }
/**
 * \@name calculatePosition
 * @return {?}
 */
private calculatePosition(): ClientRect {
        return this.tagInput.inputForm.getElementPosition();
    }
/**
 * \@name requestAdding
 * @param item {Ng2MenuItem}
 */
private requestAdding = (item: Ng2MenuItem): void => {
        this.tagInput.onAddingRequested(true, this.createTagModel(item));
    }
/**
 * \@name createTagModel
 * @param {?} item
 * @return {?}
 */
private createTagModel(item: Ng2MenuItem): TagModel {
        const /** @type {?} */ display = typeof item.value === 'string' ? item.value : item.value[this.displayBy];
        const /** @type {?} */ value = typeof item.value === 'string' ? item.value : item.value[this.identifyBy];

        return {
            ...item.value,
            [this.tagInput.displayBy]: display,
            [this.tagInput.identifyBy]: value
        };
    }
/**
 *
 * @param {?} value {string}
 * @return {?}
 */
private getMatchingItems(value: string): TagModel[] {
        if (!value && !this.showDropdownIfEmpty) {
            return [];
        }

        const /** @type {?} */ dupesAllowed = this.tagInput.allowDupes;

        return this.autocompleteItems.filter((item: TagModel) => {
            const /** @type {?} */ hasValue: boolean = dupesAllowed ? false : this.tagInput.tags.some(tag => {
                const /** @type {?} */ identifyBy = this.tagInput.identifyBy;
                const /** @type {?} */ model = typeof tag.model === 'string' ? tag.model : tag.model[identifyBy];

                return model === item[this.identifyBy];
            });

            return this.matchingFn(value, item) && (hasValue === false);
        });
    }
/**
 * \@name setItems
 * @param {?} items
 * @return {?}
 */
private setItems(items: TagModel[]): void {
        this.items = items.slice(0, this.limitItemsTo || items.length);
    }
/**
 * \@name resetItems
 */
private resetItems = (): void => {
        this.items = [];
    }
/**
 * \@name populateItems
 * @param {?} data
 * @return {?}
 */
private populateItems(data: any): TagInputDropdown {
        this.autocompleteItems = data.map(item => {
            return typeof item === 'string' ? {
                [this.displayBy]: item,
                [this.identifyBy]: item
            } : item;
        });

        return this;
    }
/**
 * \@name getItemsFromObservable
 * @param text
 */
private getItemsFromObservable = (text: string): void => {
        this.setLoadingState(true);

        const subscribeFn = (data: any[]) => {
            // hide loading animation
            this.setLoadingState(false)
                // add items
                .populateItems(data);

            this.setItems(this.getMatchingItems(text));

            if (this.items.length) {
                this.dropdown.show(this.calculatePosition());
            } else if (!this.showDropdownIfEmpty && this.isVisible) {
                this.dropdown.hide();
            }
        };

        this.autocompleteObservable(text)
            .first()
            .subscribe(subscribeFn, () => this.setLoadingState(false));
    }
/**
 * \@name setLoadingState
 * @param {?} state
 * @return {?}
 */
private setLoadingState(state: boolean): TagInputDropdown {
        this.tagInput.isLoading = state;

        return this;
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'tag-input-dropdown',
    template: `
      <ng2-dropdown>
          <ng2-dropdown-menu [focusFirstElement]="focusFirstElement"
                             [appendToBody]="appendToBody"
                             [offset]="offset">
              <ng2-menu-item *ngFor="let item of items; let index = index; let last = last"
                             [value]="item"
                             [ngSwitch]="!!templates.length">

                  <span *ngSwitchCase="false"
                        [innerHTML]="item[displayBy] | highlight : tagInput.inputForm.value.value">
                  </span>

                  <ng-template *ngSwitchDefault
                            [ngTemplateOutlet]="templates.first"
                            [ngTemplateOutletContext]="{ item: item, index: index, last: last }">
                  </ng-template>
              </ng2-menu-item>
          </ng2-dropdown-menu>
      </ng2-dropdown>
    `
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Injector, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'dropdown': [{ type: ViewChild, args: [Ng2Dropdown, ] },],
'templates': [{ type: ContentChildren, args: [TemplateRef, ] },],
'offset': [{ type: Input },],
'focusFirstElement': [{ type: Input },],
'showDropdownIfEmpty': [{ type: Input },],
'autocompleteObservable': [{ type: Input },],
'minimumTextLength': [{ type: Input },],
'limitItemsTo': [{ type: Input },],
'displayBy': [{ type: Input },],
'identifyBy': [{ type: Input },],
'matchingFn': [{ type: Input },],
'appendToBody': [{ type: Input },],
'keepOpen': [{ type: Input },],
'autocompleteItems': [{ type: Input },],
'scrollListener': [{ type: HostListener, args: ['window:scroll', ] },],
'onWindowBlur': [{ type: HostListener, args: ['window:blur', ] },],
};
}

function TagInputDropdown_tsickle_Closure_declarations() {
/** @type {?} */
TagInputDropdown.decorators;
/**
 * @nocollapse
 * @type {?}
 */
TagInputDropdown.ctorParameters;
/** @type {?} */
TagInputDropdown.propDecorators;
/**
 * \@name dropdown
 * @type {?}
 */
TagInputDropdown.prototype.dropdown;
/**
 * \@name menuTemplate
 * @desc reference to the template if provided by the user
 * @type {?}
 */
TagInputDropdown.prototype.templates;
/**
 * \@name offset
 * @type {?}
 */
TagInputDropdown.prototype.offset;
/**
 * \@name focusFirstElement
 * @type {?}
 */
TagInputDropdown.prototype.focusFirstElement;
/**
 * - show autocomplete dropdown if the value of input is empty
 * \@name showDropdownIfEmpty
 * @type {?}
 */
TagInputDropdown.prototype.showDropdownIfEmpty;
/**
 * \@description observable passed as input which populates the autocomplete items
 * \@name autocompleteObservable
 * @type {?}
 */
TagInputDropdown.prototype.autocompleteObservable;
/**
 * - desc minimum text length in order to display the autocomplete dropdown
 * \@name minimumTextLength
 * @type {?}
 */
TagInputDropdown.prototype.minimumTextLength;
/**
 * - number of items to display in the autocomplete dropdown
 * \@name limitItemsTo
 * @type {?}
 */
TagInputDropdown.prototype.limitItemsTo;
/**
 * \@name displayBy
 * @type {?}
 */
TagInputDropdown.prototype.displayBy;
/**
 * \@name identifyBy
 * @type {?}
 */
TagInputDropdown.prototype.identifyBy;
/**
 * \@description a function a developer can use to implement custom matching for the autocomplete
 * \@name matchingFn
 * @type {?}
 */
TagInputDropdown.prototype.matchingFn;
/**
 * \@name appendToBody
 * @type {?}
 */
TagInputDropdown.prototype.appendToBody;
/**
 * \@name keepOpen
 * \@description option to leave dropdown open when adding a new item
 * @type {?}
 */
TagInputDropdown.prototype.keepOpen;
/**
 * list of items that match the current value of the input (for autocomplete)
 * \@name items
 * @type {?}
 */
TagInputDropdown.prototype.items;
/**
 * \@name tagInput
 * @type {?}
 */
TagInputDropdown.prototype.tagInput;
/**
 * \@name _autocompleteItems
 * @type {?}
 */
TagInputDropdown.prototype._autocompleteItems;
/**
 *
 * \@name show
 * @type {?}
 */
TagInputDropdown.prototype.show;
/**
 * \@name requestAdding
 * \@param item {Ng2MenuItem}
 * @type {?}
 */
TagInputDropdown.prototype.requestAdding;
/**
 * \@name resetItems
 * @type {?}
 */
TagInputDropdown.prototype.resetItems;
/**
 * \@name getItemsFromObservable
 * \@param text
 * @type {?}
 */
TagInputDropdown.prototype.getItemsFromObservable;
/** @type {?} */
TagInputDropdown.prototype.injector;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
